import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../config/api';

interface Stats {
  totalLeads: number;
  newLeads: number;
  contactedLeads: number;
  wonLeads: number;
}

interface RecentLead {
  id: number;
  company_name: string;
  contact_name: string;
  brand_id: string;
  status: string;
  created_at: string;
}

export function DashboardPage() {
  const [stats, setStats] = useState<Stats>({
    totalLeads: 0,
    newLeads: 0,
    contactedLeads: 0,
    wonLeads: 0,
  });
  const [recentLeads, setRecentLeads] = useState<RecentLead[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(getApiUrl('/api/leads/stats'), {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
        setRecentLeads(data.recentLeads || []);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'New Leads', value: stats.newLeads, icon: '🆕', color: 'bg-green-600', href: '/leads?status=new' },
    { label: 'Contacted', value: stats.contactedLeads, icon: '✉️', color: 'bg-yellow-600', href: '/leads?status=contacted' },
    { label: 'Won', value: stats.wonLeads, icon: '🏆', color: 'bg-purple-600', href: '/leads?status=won' },
    { label: 'Total Leads', value: stats.totalLeads, icon: '📋', color: 'bg-blue-600', href: '/leads' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Dashboard</h1>
        <p className="text-dark-text-secondary text-sm sm:text-base">Welcome back! Here's an overview of the leads.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <Link
            key={stat.label}
            to={stat.href}
            className="card p-6 block transition-colors hover:border-blue-600"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center text-2xl`}>
                {stat.icon}
              </div>
            </div>
            <div>
              <p className="text-dark-text-secondary text-sm mb-1">{stat.label}</p>
              <p className="text-3xl font-bold text-white">
                {loading ? '...' : stat.value}
              </p>
            </div>
          </Link>
        ))}
      </div>

      {/* Recent Leads */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">Recent Leads</h2>
          <Link to="/leads" className="text-sm text-blue-400 hover:underline">
            View all →
          </Link>
        </div>
        <div className="space-y-4">
          {loading ? (
            <p className="text-dark-text-secondary text-center py-8">
              Loading...
            </p>
          ) : recentLeads.length === 0 ? (
            <p className="text-dark-text-secondary text-center py-8">
              No leads yet
            </p>
          ) : (
            <div className="space-y-3">
              {recentLeads.map((lead) => (
                <Link
                  key={lead.id}
                  to={`/leads?lead=${lead.id}`}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-4 bg-dark-bg rounded-lg transition-colors hover:bg-slate-700"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white truncate">{lead.company_name}</div>
                    <div className="text-sm text-dark-text-secondary truncate">{lead.contact_name}</div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-dark-text-secondary capitalize">
                      {lead.brand_id.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      lead.status === 'new' ? 'bg-green-900/50 text-green-400' :
                      lead.status === 'contacted' ? 'bg-blue-900/50 text-blue-400' :
                      lead.status === 'quoted' ? 'bg-yellow-900/50 text-yellow-400' :
                      lead.status === 'won' ? 'bg-purple-900/50 text-purple-400' :
                      'bg-red-900/50 text-red-400'
                    }`}>
                      {lead.status}
                    </span>
                    <span className="text-xs text-dark-text-secondary">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
